import { STANDARD_BRANCH } from '@festina-life/api'
import { dateUtils } from '@festina-life/core'
import { Group } from '@visx/group'

import { useDraftContext } from '../../context/draftContext'
import { useParameterContext } from '../../context/parameterContext'
import { useParameterDefinitionId } from '../../hooks/useParameterDefinitionId'
import { useNavigationParams } from '../../system/useNavigationParams'
import { getParameterDefinitionInOut } from '../../uiModels/parameterOperations/helpers'
import type { IndexedParameterDefinitionCreateOperationWithMetaData } from '../../uiModels/parameterOperations/types'
import { useIsPersonaEditor } from '../../uiModels/useIsPersonaEditor'
import { NO_DATE_SYMBOL } from '../../util/constants'
import type { ChildProps } from '../versionedTimeline/VersionedArrowsGroup'
import { VersionedTimeline } from '../versionedTimeline/VersionedTimeline'
import { ParameterVersionCircle } from './ParameterVersionCircle'

export const ParameterDefinitionVersionsLine = ({
  versions,
  lineStartDate,
  lineEndDate,
  lineLength,
}: ChildProps<IndexedParameterDefinitionCreateOperationWithMetaData>) => {
  const { parameterDefinitionOperation } = useParameterContext()
  const { onConfigureNewDraft } = useDraftContext()
  const currentParameterDefinitionEntity =
    parameterDefinitionOperation?.internalOperation.entity
  const parameterDefinitionId = useParameterDefinitionId()
  const { branch, layer } = useNavigationParams()
  const isEditor = useIsPersonaEditor()

  const handleCreateDraft = (fromDate: Date) => {
    onConfigureNewDraft(
      {
        type: 'parameterDefinition',
        value: {
          fromDate,
          parameterId: parameterDefinitionId,
          copyParameter:
            parameterDefinitionOperation &&
            currentParameterDefinitionEntity?.name
              ? {
                  fromDate: currentParameterDefinitionEntity.fromDate
                    ? dateUtils
                        .dateUtcAsLocal(
                          currentParameterDefinitionEntity.fromDate,
                        )
                        .toDate()
                    : undefined,
                  name: currentParameterDefinitionEntity.name,
                  layer: parameterDefinitionOperation.layer,
                  block: parameterDefinitionOperation.block,
                }
              : undefined,
          packagePath:
            parameterDefinitionOperation?.internalOperation.entity?.packageName?.split(
              '.',
            ) ?? [],
        },
      },
      parameterDefinitionOperation?.layer ?? layer,
    )
  }

  return (
    <Group>
      <VersionedTimeline
        lengthInPixels={lineLength}
        startDate={lineStartDate}
        endDate={lineEndDate}
        versions={versions}
        versionDate={(v) =>
          getParameterDefinitionInOut(v).fromDate ?? NO_DATE_SYMBOL
        }
        onCreateDraft={
          branch && branch !== STANDARD_BRANCH && isEditor
            ? handleCreateDraft
            : undefined
        }
        renderDateVersions={({ date, versions, dateToPixel }) => (
          <ParameterVersionCircle
            key={date.toString()}
            versions={versions}
            dateToPixel={dateToPixel}
          />
        )}
      />
    </Group>
  )
}
